import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { generateClient } from 'aws-amplify/api';
import { enqueueSnackbar } from 'notistack';
import { useEffect, useMemo, useState } from 'react';
import { segmentByTenant } from '../../graphql/queries';
import { fetchAllPages } from '../../tools/graphqlPaging';

/** Shown when the signed-in tenant has no saved segments at all. */
export const NO_SEGMENTS_MESSAGE = 'No saved segments are available';

/** Shown (and snackbarred) when the segment fetch fails. */
export const LOAD_FAILED_MESSAGE = 'Unable to load segments';

/**
 * Saved-segment selector for the Bulk Edit page.
 *
 * Self-contained: it owns the fetch and its own loading / error / empty states
 * and knows nothing about Formik or the query builder. Selection is controlled
 * by the parent, which is what lets the page hold back a selection until a load
 * is actually applied (so cancelling a replacement confirmation leaves the
 * input showing the prior selection with no extra work here).
 *
 * Every retrieval is scoped to `tenantId` — the only variable sent with
 * `segmentByTenant` — so one tenant can never see another's segments.
 *
 * @param {object} props
 * @param {string} props.tenantId Tenant of the signed-in user.
 * @param {object|null} props.value Controlled selection (a Segment or null).
 * @param {(segment: object|null) => void} props.onChange Emitted on a user
 *   selection, and with `null` when the input is cleared. Never emitted for a
 *   failed or in-flight fetch.
 * @param {boolean} [props.disabled] Disables the input regardless of state.
 */
export function SegmentPicker({ tenantId, value, onChange, disabled = false }) {
	// One client for the lifetime of the component; recreating it per render
	// would also re-trigger the fetch effect.
	const client = useMemo(() => generateClient(), []);
	const [segments, setSegments] = useState([]);
	const [loading, setLoading] = useState(false);
	const [failed, setFailed] = useState(false);

	useEffect(() => {
		// `active` drops the response of a superseded tenant so a slow first
		// fetch cannot overwrite the options of the tenant now selected.
		let active = true;

		if (!tenantId) {
			setSegments([]);
			setLoading(false);
			setFailed(false);
			return undefined;
		}

		async function loadSegments() {
			setLoading(true);
			setFailed(false);

			try {
				const allSegments = await fetchAllPages({
					client,
					query: segmentByTenant,
					resultKey: 'segmentByTenant',
					variables: { tenant: tenantId },
				});

				if (!active) return;

				// The GSI already sorts on `name`, so pages arrive ordered. Sorting
				// here makes the ordering an invariant of this component rather than
				// of the index, and keeps it stable across page boundaries.
				setSegments(
					[...allSegments].sort((a, b) =>
						(a?.name ?? '').localeCompare(b?.name ?? ''),
					),
				);
			} catch (err) {
				if (!active) return;

				// No onChange on this path: the page's query field stays untouched.
				console.error('Error loading segments', err);
				enqueueSnackbar(LOAD_FAILED_MESSAGE, { variant: 'error' });
				setSegments([]);
				setFailed(true);
			} finally {
				if (active) {
					setLoading(false);
				}
			}
		}

		loadSegments();

		return () => {
			active = false;
		};
	}, [tenantId, client]);

	const isEmpty = !loading && !failed && segments.length === 0;
	// Nothing is selectable while fetching, after a failure, or with no
	// segments, so the input is closed to selection input in all three.
	const isDisabled = disabled || loading || failed || segments.length === 0;

	let helperText = ' ';
	if (failed) {
		helperText = LOAD_FAILED_MESSAGE;
	} else if (isEmpty) {
		helperText = NO_SEGMENTS_MESSAGE;
	}

	return (
		<Autocomplete
			size="small"
			options={segments}
			value={value ?? null}
			onChange={(event, newValue) => onChange(newValue ?? null)}
			getOptionLabel={(option) => option?.name ?? ''}
			isOptionEqualToValue={(option, selected) => option?.id === selected?.id}
			loading={loading}
			disabled={isDisabled}
			loadingText="Loading segments..."
			noOptionsText={isEmpty ? NO_SEGMENTS_MESSAGE : 'No matching segments'}
			renderOption={(props, option) => (
				<li {...props} key={option.id}>
					{option.name}
				</li>
			)}
			renderInput={(params) => (
				<TextField
					{...params}
					label="Load from saved segment"
					placeholder="Select a segment"
					helperText={helperText}
					InputProps={{
						...params.InputProps,
						endAdornment: (
							<>
								{loading ? (
									<CircularProgress
										aria-label="Loading segments"
										color="inherit"
										size={18}
									/>
								) : null}
								{params.InputProps.endAdornment}
							</>
						),
					}}
				/>
			)}
		/>
	);
}
